//Remembers each member's last known "may I open this?" answer per rule set, in localStorage, so:
//  - several tabs (and quick page reloads) share one answer instead of each asking the server, and
//  - when the server or Keap is briefly unavailable the page keeps showing what it last knew
//    instead of greying out calls the member is entitled to join.
//Only a convenience: it is never the source of truth. Every function is safe to call with storage
//blocked, full or corrupt, and then simply remembers nothing.

const KEY_PREFIX = 'cwu:ruleAccess:v1:';
//Fresh enough to reuse WITHOUT asking the server again (the other tabs just asked).
export const SHARE_MS = 2 * 60 * 1000;
//The oldest answer still used when the server cannot be reached.
export const FALLBACK_MS = 24 * 60 * 60 * 1000;

//Who is signed in, read from the token's own payload (no network). Null when unknown, and then
//nothing is stored, so one member's answers can never be shown to another on a shared computer.
export const currentUserKey = () => {
  try {
    const token = window.localStorage.getItem('CWAuthToken');
    if (!token) return null;
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    const id = payload.id || payload.user_id || payload.sub || payload.email;
    return id ? String(id) : null;
  } catch (error) {
    return null;
  }
};

const load = (userKey) => {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(KEY_PREFIX + userKey) || '{}'
    );
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

//For the given rule set ids: `fresh` answers (reuse as is) and `usable` answers (fallback only).
//Both are maps of rule_set_id -> boolean; an id with no remembered answer is simply absent.
export const readAnswers = (ruleSetIds, now = Date.now()) => {
  const out = { fresh: {}, usable: {} };
  const userKey = currentUserKey();
  if (!userKey) return out;
  const saved = load(userKey);
  for (const id of ruleSetIds) {
    const entry = saved[id];
    if (!entry || typeof entry.p !== 'boolean' || typeof entry.at !== 'number')
      continue;
    const age = now - entry.at;
    if (age < 0 || age > FALLBACK_MS) continue;
    out.usable[id] = entry.p;
    //Only a "yes" is shared between tabs: a member who has just gained access must not be told
    //"no" for two more minutes by an answer they already had.
    if (age <= SHARE_MS && entry.p === true) out.fresh[id] = true;
  }
  return out;
};

//`evaluations` is the server payload: [{ rule_set_id, permission }].
export const saveAnswers = (evaluations, now = Date.now()) => {
  const userKey = currentUserKey();
  if (!userKey || !Array.isArray(evaluations)) return;
  try {
    const saved = load(userKey);
    for (const item of evaluations) {
      if (item && typeof item.permission === 'boolean')
        saved[item.rule_set_id] = { p: item.permission, at: now };
    }
    window.localStorage.setItem(KEY_PREFIX + userKey, JSON.stringify(saved));
  } catch (error) {
    //storage full or blocked: remember nothing
  }
};

export const toEvaluations = (map) =>
  Object.keys(map).map((id) => ({
    rule_set_id: isNaN(Number(id)) ? id : Number(id),
    permission: map[id]
  }));
